import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import moment from "moment";
import { useMemo } from "react";
import { Calendar as RBCalendar, Views, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { CalendarView, GenericCalendarEntry } from "./Calendar";
import { RBCShowMoreButtonCustom } from "./RectBigCalendarCustomComponents/RBCShowMoreButtonCustom";

const MONTHS = moment.months();

const calendarLocalizer = momentLocalizer(moment);

export function YearCalendarView<TEntry extends GenericCalendarEntry>({
  date,
  events,
  onSelectEvent,
  renderEntryTitle,
  eventPropGetter,
  onDrilldown,
}: {
  date: Date;
  events: TEntry[];
  onSelectEvent?: (event: TEntry) => void;
  renderEntryTitle?: (entry: TEntry) => React.ReactNode;
  eventPropGetter?: any;
  onDrilldown?: (date: Date) => void;
}) {
  // Group events by month
  const eventsByMonth = useMemo(() => {
    const grouped: Record<number, TEntry[]> = {};
    const year = moment(date).year();
    for (let m = 0; m < 12; m++) {
      const monthStart = moment({ year, month: m, day: 1 }).startOf("month");
      const monthEnd = monthStart.clone().endOf("month");
      grouped[m] = events.filter((entry) => {
        const entryStart = moment(entry.start);
        const entryEnd = moment(entry.end);
        // Event overlaps this month if it starts before monthEnd and ends after monthStart
        return entryStart.isBefore(monthEnd) && entryEnd.isAfter(monthStart);
      });
    }
    return grouped;
  }, [events, date]);

  return (
    <Box className='custom-rbc-year-view'>
      <Grid container spacing={2}>
        {MONTHS.map((month, idx) => {
          const monthDate = moment(date).month(idx).startOf("month").toDate();
          const monthEvents = eventsByMonth[idx] || [];
          return (
            <Grid
              size={{ xs: 12, lg: 6 }}
              key={month}
              sx={{
                ".rbc-event": {
                  height: "23px !important",
                },
              }}
            >
              <Box sx={{ mb: 1, textAlign: "center" }}>
                <strong>{month}</strong>
              </Box>
              <RBCalendar
                style={{ height: 600 }}
                events={monthEvents}
                localizer={calendarLocalizer}
                date={monthDate}
                view={Views.MONTH}
                onDrillDown={onDrilldown}
                drilldownView={CalendarView.Day}
                getDrilldownView={undefined}
                views={[Views.MONTH]}
                toolbar={false}
                popup={true}
                onSelectEvent={onSelectEvent}
                eventPropGetter={eventPropGetter}
                components={{
                  showMore: RBCShowMoreButtonCustom,
                  // dateCellWrapper: (props) => (
                  //   <YearCalendarDateCellWrapper {...props} onDrilldown={onDrilldown} />
                  // ),
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

// Assign static properties for react-big-calendar compatibility
(YearCalendarView as any).title = (date: Date) => `${moment(date).year()}`;
(YearCalendarView as any).navigate = (date: Date, action: string) => {
  const m = moment(date);
  switch (action) {
    case "PREV":
      return m.clone().subtract(1, "year").toDate();
    case "NEXT":
      return m.clone().add(1, "year").toDate();
    case "TODAY":
      return moment().toDate();
    default:
      return date;
  }
};
(YearCalendarView as any).range = (date: Date) => {
  const start = moment(date).startOf("year").toDate();
  const end = moment(date).endOf("year").toDate();
  return [start, end];
};

// Non-generic wrapper for react-big-calendar compatibility
export const YearCalendarViewRBC: React.ComponentType<any> &
  import("react-big-calendar").ViewStatic = YearCalendarView as any;
YearCalendarViewRBC.title = (YearCalendarView as any).title;
YearCalendarViewRBC.navigate = (YearCalendarView as any).navigate;
// @ts-expect-error: react-big-calendar custom view static 'range' property is required at runtime
YearCalendarViewRBC.range = (YearCalendarView as any).range;
